import React, { createContext, useContext, useState, useEffect } from 'react';

const ConversationActivitiesContext = createContext();

export const useConversationActivities = () => {
  const context = useContext(ConversationActivitiesContext);
  if (!context) {
    throw new Error('useConversationActivities must be used within a ConversationActivitiesProvider');
  }
  return context;
};

// Custom hook for managing localStorage filters
const useLocalStorageFilters = (key, defaultValue) => {
  const [value, setValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  const setStoredValue = (newValue) => {
    try {
      setValue(newValue);
      window.localStorage.setItem(key, JSON.stringify(newValue));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [value, setStoredValue];
};

export const ConversationActivitiesProvider = ({ children }) => {
  // Filter states with localStorage persistence
  const [searchTerm, setSearchTerm] = useLocalStorageFilters('conversationActivities_searchTerm', '');
  const [perPage] = useState(20); // Always 20 per page
  const [showUnseenOnly, setShowUnseenOnly] = useLocalStorageFilters('conversationActivities_showUnseenOnly', false);
  const [startDate, setStartDate] = useLocalStorageFilters('conversationActivities_startDate', '');
  const [endDate, setEndDate] = useLocalStorageFilters('conversationActivities_endDate', '');
  const [conversationIdFilter, setConversationIdFilter] = useLocalStorageFilters('conversationActivities_conversationIdFilter', '');
  const [clinicFilter, setClinicFilter] = useLocalStorageFilters('conversationActivities_clinicFilter', '');

  // Clear all filters function
  const clearAllFilters = () => {
    setSearchTerm('');
    setShowUnseenOnly(false);
    setStartDate('');
    setEndDate('');
    setConversationIdFilter('');
    setClinicFilter('');
  };

  // Check if any filters are active
  const hasActiveFilters = searchTerm || showUnseenOnly || startDate || endDate || conversationIdFilter || clinicFilter;

  // Get all filter values as an object for API calls
  const getFilterParams = () => {
    const params = {};

    if (searchTerm) params.search = searchTerm;
    if (showUnseenOnly) params.seen = 'unseen';
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (conversationIdFilter) params.conversation_id = conversationIdFilter;
    if (clinicFilter) params.clinic_id = clinicFilter;

    return params;
  };

  const value = {
    // Filter states
    searchTerm,
    setSearchTerm,
    perPage,
    showUnseenOnly,
    setShowUnseenOnly,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    conversationIdFilter,
    setConversationIdFilter,
    clinicFilter,
    setClinicFilter,

    // Actions
    clearAllFilters,
    hasActiveFilters,
    getFilterParams,
  };

  return (
    <ConversationActivitiesContext.Provider value={value}>
      {children}
    </ConversationActivitiesContext.Provider>
  );
};
